'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

/**
 * Final CTA — "Ready to enhance your materials?"
 *
 * Matches the dark-navy reference composition: centered type stack, blue
 * emphasis line, primary + outline actions, email beneath. The section
 * background is the supplied get-started-bg.png artwork (its own molecular
 * corners and dotted wave) under a navy contrast overlay.
 *
 * Entrance reveal is progressive: content is fully visible by default (SSR and
 * no-JS users never see hidden text); once JS runs, an IntersectionObserver
 * arms the standard `.rise` stagger the first time the section scrolls into
 * view. The wave is static — a continuously moving decoration would fight the
 * site's restrained-motion rules — and `prefers-reduced-motion` disables the
 * reveal entirely via the global media block.
 */
const FinalCTA = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      // Low threshold: on short phone screens this tall section may never reach
      // a high visible fraction, which would silently suppress the reveal.
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /** Reveal classes are only attached once in view — never hide-first. */
  const reveal = (delay?: string) => (inView ? `rise ${delay ?? ''}` : '');

  return (
    <section ref={sectionRef} className="relative overflow-hidden bg-ink text-white">
      {/* Background artwork (carries its own molecular corners and dotted
          wave) + navy overlay for contrast; stronger below sm. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: "url('/assets/images/get-started-bg.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-ink/45 sm:bg-ink/30" />

      <div className="container-page relative pb-[clamp(8rem,15vw,14rem)] pt-[clamp(3.5rem,7vw,6rem)]">
        <div className="mx-auto max-w-3xl text-center">
          <p
            className={`${reveal()} text-[0.8125rem] font-semibold uppercase tracking-[0.22em] text-accent`}
          >
            Get started
          </p>

          <h2 className={`${reveal('delay-1')} t-display mt-5 text-white`}>
            Ready to enhance
            <span className="text-accent-grad block">your materials?</span>
          </h2>

          <p className={`${reveal('delay-2')} t-body mx-auto mt-6 max-w-2xl text-slate-300`}>
            Request a technical consultation or a sample kit to evaluate graphene additives in your
            own formulation.
          </p>

          <div
            className={`${reveal('delay-3')} mx-auto mt-10 flex max-w-md flex-col items-stretch justify-center gap-4 sm:max-w-none sm:flex-row sm:items-center`}
          >
            <Link
              href="/contact"
              className="group inline-flex min-h-[56px] items-center justify-center gap-3 rounded-lg bg-accent px-8 text-base font-semibold text-accent-foreground shadow-[0_2px_14px_rgba(14,165,233,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-[background-color,box-shadow,transform] duration-fast ease-out hover:bg-sky-400 hover:shadow-[0_6px_24px_rgba(14,165,233,0.45)] active:translate-y-px active:scale-[0.99] motion-reduce:active:transform-none"
            >
              Contact us
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
                className="transition-transform duration-fast ease-out group-hover:translate-x-1 motion-reduce:transform-none"
              >
                <path
                  d="M3 10h13M11 4.5 16.5 10 11 15.5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>

            <Link
              href="/services"
              className="inline-flex min-h-[56px] items-center justify-center gap-3 rounded-lg border border-white/20 bg-white/[0.03] px-8 text-base font-semibold text-white backdrop-blur-[2px] transition-[border-color,background-color] duration-fast ease-out hover:border-white/45 hover:bg-white/[0.08] active:translate-y-px active:scale-[0.99] motion-reduce:active:transform-none"
            >
              View technical specifications
              <Icon name="DocumentTextIcon" size={19} className="shrink-0" />
            </Link>
          </div>

          <a
            href="mailto:operations@micratto.com"
            className={`${reveal('delay-4')} mt-10 inline-flex items-center gap-2.5 break-all text-[0.9375rem] text-slate-400 transition-colors duration-fast hover:text-white`}
          >
            <Icon name="EnvelopeIcon" size={19} className="shrink-0" />
            operations@micratto.com
          </a>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
